/**
 * Activité mensuelle et effectif — M11 (retour Manon, mi-parcours).
 *
 * « Mettre le nombre d'agents présents par mois sur le graphique, pour se
 *   rendre compte du travail accompli en fonction de l'effectif, quitte à
 *   mettre un pourcentage. » Barres = interventions réalisées ; sous chaque
 *   barre, les agents, le taux de présence et le ratio par agent présent.
 * Chiffres destinés au conseil : les définitions sont écrites sous le graphique.
 */
import { Card } from '@/components/ui';
import type { DashboardDetail } from '@/lib/api';

type Activite = NonNullable<DashboardDetail['activiteMensuelle']>;

export function ActiviteMensuelleCard({ activite, accent }: { activite: Activite; accent: string }) {
  const mois = activite.mois;
  const max = Math.max(1, ...mois.map((m) => m.interventions));
  const total = mois.reduce((s, m) => s + m.interventions, 0);
  const heures = mois.reduce((s, m) => s + m.heures, 0);

  return (
    <Card variant="glass" className="p-4">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <div className="font-display text-sm font-semibold text-ink">{activite.titre}</div>
        <div className="ml-auto text-[11px] text-ink-subtle">
          12 derniers mois · {total} interventions · {heures.toLocaleString('fr-FR')} h
        </div>
      </div>

      {/* Défilement horizontal sur petit écran : 12 colonnes lisibles. */}
      <div className="overflow-x-auto">
        <div className="grid min-w-[640px] grid-cols-12 gap-1.5">
          {mois.map((m) => (
            <div key={m.mois} className="flex flex-col items-center">
              <div className="flex h-28 w-full items-end">
                <div
                  className="w-full rounded-t"
                  title={`${m.label} : ${m.interventions} interventions, ${m.heures} h, ${m.effectif} agents`}
                  style={{
                    height: `${Math.max(3, (m.interventions / max) * 100)}%`,
                    background: `linear-gradient(180deg, oklch(var(--c-${accent}) / 0.7), oklch(var(--c-${accent}) / 0.3))`,
                    borderTop: `1px solid oklch(var(--c-${accent}) / 0.6)`,
                  }}
                />
              </div>
              <div className="mt-1 text-xs font-semibold tabular-nums text-ink">{m.interventions}</div>
              <div className="text-[10px] text-ink-subtle">{m.label}</div>
              {/* L'annotation demandée : l'effectif du mois, sous la barre. */}
              <div className="mt-1 text-center text-[10px] leading-tight text-ink-muted">
                <div title="Effectif du service (agents mobilisés)">👷 {m.effectif}{m.mobilises ? ` (${m.mobilises})` : ''}</div>
                {m.tauxPresence !== null && <div title="Taux de présence">{m.tauxPresence} %</div>}
                {m.interventionsParAgent !== null && (
                  <div title="Interventions par agent présent (équivalent temps plein)">{m.interventionsParAgent.toLocaleString('fr-FR')}/ag.</div>
                )}
                {m.heures > 0 && <div title="Heures d'intervention">{m.heures.toLocaleString('fr-FR')} h</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <details className="mt-3 text-[11px] text-ink-subtle">
        <summary className="cursor-pointer">Comment ces chiffres sont calculés</summary>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          {activite.definitions.map((d) => <li key={d}>{d}</li>)}
        </ul>
      </details>
    </Card>
  );
}
